import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import Navigation from '@/components/navigation';
import { Card } from '@/components/ui/card';
import { Play, X } from 'lucide-react';
import BrandHeader from '@/components/BrandHeader';
import { getDisplayName } from '@/lib/displayName';
import parse from 'html-react-parser';
import {
  articleIdFromSearch,
  resolveHeroImageUrl,
  resolveContentImageUrl,
  resolveThumbnailUrl,
  stripHtml,
} from '@/lib/contentImage';
import {
  DEMO_ARTICLE_ID,
  isImageCardArticle,
  pickFeaturedLearning,
  presentSauceArticles,
  usesSharedSequence,
  withBundledUltraArticle,
} from '@/lib/demoArticle';
import { isVisibleOnSauce, journeyDayForMember } from '@shared/imageArticle';

type TabKey = 'all' | 'hacks' | 'clips' | 'ultra';

type Article = {
  id: string;
  readTime: string;
  title: string;
  subtitle: string;
  preview: string;
  category: string;
  heroBg: string;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  tags: { label: string; color: string }[];
  bodyText?: string | null;
  linkUrl?: string;
  imageCard?: boolean;
};

interface LearningContent {
  id: string;
  headline: string;
  subHeading?: string | null;
  bodyText?: string | null;
  imageUrl: string | null;
  thumbnailUrl?: string | null;
  headerImage?: string | null;
  linkUrl: string;
  section?: string;
  isFeatured?: boolean;
  featureDay?: number | null;
  sequence?: 'journey' | 'shared';
}

function truncatePreview(text: string | null | undefined, max = 55): string {
  const plain = stripHtml(text);
  if (!plain) return 'Why water alone might not be enough to keep…';
  if (plain.length <= max) return plain;
  const slice = plain.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  return `${(lastSpace > 20 ? slice.slice(0, lastSpace) : slice).trim()}…`;
}

function contentImageUrl(item?: Partial<LearningContent>): string | null {
  return resolveContentImageUrl(item);
}

function isClip(item: LearningContent): boolean {
  return (
    item.section === 'videos' ||
    Boolean(item.linkUrl?.includes('youtube')) ||
    Boolean(item.linkUrl?.includes('youtu.be'))
  );
}

function ContentImage({
  item,
  className,
  fallback,
  variant = 'thumb',
  testId,
}: {
  item?: Partial<LearningContent>;
  className?: string;
  fallback: React.ReactNode;
  variant?: 'thumb' | 'hero';
  testId?: string;
}) {
  const src = variant === 'hero' ? resolveHeroImageUrl(item) : contentImageUrl(item);
  if (src) {
    return (
      <img
        src={src}
        alt={item?.headline || 'Content'}
        className={className}
        data-testid={testId}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }
  return <>{fallback}</>;
}

const HERO_BGS = ['bg-[#d93838]', 'bg-[#f59e3b]', 'bg-[#fbbf24]', 'bg-[#a855f7]'];
const CLIP_COLORS = [
  'bg-[#fde68a]',
  'bg-[#f59e3b]',
  'bg-[#fef3c7]',
  'bg-[#fde68a]',
  'bg-[#bae6fd]',
  'bg-[#7dd3fc]',
  'bg-[#fb923c]',
  'bg-[#ea580c]',
  'bg-[#d9f99d]',
  'bg-[#fbcfe8]',
  'bg-[#fecaca]',
  'bg-[#e11d48]',
];

function toArticle(item: LearningContent, index: number): Article {
  const isDemo = item.id === DEMO_ARTICLE_ID;
  const imageCard = isImageCardArticle(item);
  const mins =
    isDemo || imageCard
      ? 2
      : item.bodyText
        ? Math.max(3, Math.min(12, Math.ceil(item.bodyText.length / 800)))
        : 7;
  return {
    id: item.id,
    readTime: `${mins} MIN READ`,
    title: item.headline,
    subtitle: item.subHeading || "Don't be a copy cat. Their number isn't your number.",
    preview: imageCard ? item.subHeading || '' : truncatePreview(item.bodyText, 55),
    category: item.section || 'Wellness',
    heroBg: HERO_BGS[index % HERO_BGS.length],
    imageUrl: resolveHeroImageUrl(item),
    thumbnailUrl: resolveThumbnailUrl(item),
    tags: isDemo
      ? [
          { label: 'MENTAL HEALTH', color: 'bg-lime-500' },
          { label: '2 MIN READ', color: 'bg-[#e8836a]' },
        ]
      : [
          { label: 'HYDRATION', color: 'bg-lime-500' },
          { label: 'SCIENCE', color: 'bg-[#e8836a]' },
        ],
    bodyText: item.bodyText,
    linkUrl: item.linkUrl,
    imageCard,
  };
}

const Content = () => {
  const [tab, setTab] = useState<TabKey>('all');
  const { user } = useAuth();
  const { data: userProfile } = useQuery<any>({
    queryKey: ['/api/profile'],
    retry: false,
  });
  const nickname = getDisplayName({
    ...(user as any),
    avatarName: userProfile?.avatarName || (user as any)?.profile?.avatarName,
  });
  const [activeArticle, setActiveArticle] = useState<Article | null>(null);

  const { data: allContent = [] } = useQuery<LearningContent[]>({
    queryKey: ['/api/content/learning'],
    retry: false,
  });

  const journeyDay = journeyDayForMember(
    userProfile?.profileCreatedAt || userProfile?.createdAt || (user as any)?.createdAt
  );
  const sharedSequence = usesSharedSequence(allContent);
  const blogsFromApi = allContent.filter(
    (c) => !c.section || c.section === 'blogs' || c.section === 'learning'
  );
  const blogs = sharedSequence
    ? presentSauceArticles(blogsFromApi, journeyDay)
    : withBundledUltraArticle(blogsFromApi, journeyDay);
  const visibleSection = (item: LearningContent) =>
    sharedSequence || isVisibleOnSauce(item, journeyDay);
  const knowApp = allContent.filter((c) => c.section === 'know_the_app' && visibleSection(c));
  const videos = allContent.filter((c) => c.section === 'videos' && visibleSection(c));
  const featured = pickFeaturedLearning(blogs, journeyDay);

  useEffect(() => {
    const id = articleIdFromSearch(window.location.search);
    if (!id) return;
    const source = allContent.filter(
      (c) => !c.section || c.section === 'blogs' || c.section === 'learning'
    );
    const merged = sharedSequence
      ? presentSauceArticles(source, journeyDay)
      : withBundledUltraArticle(source, journeyDay);
    const item = merged.find((c) => c.id === id) || allContent.find((c) => c.id === id);
    if (item) setActiveArticle(toArticle(item, 0));
  }, [allContent, journeyDay, sharedSequence]);

  const closeArticle = () => {
    setActiveArticle(null);
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('article')) {
        url.searchParams.delete('article');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch {
      // ignore malformed URLs in tests
    }
  };

  if (activeArticle) {
    return <ArticleReader article={activeArticle} nickname={nickname} onClose={closeArticle} />;
  }

  return (
    <div className="min-h-screen pb-28 bg-cream">
      <div className="max-w-md mx-auto px-5 pt-6 pb-2 space-y-5">
        <BrandHeader title="Sauce" nickname={nickname} />

        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">
          {(
            [
              { key: 'all', label: 'All' },
              { key: 'hacks', label: 'Hacks' },
              { key: 'clips', label: 'Clips' },
              { key: 'ultra', label: 'Ultra' },
            ] as { key: TabKey; label: string }[]
          ).map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-6 py-2 rounded-full border text-sm font-semibold transition-all shrink-0 ${
                  active
                    ? 'bg-purple-500 text-white border-purple-500 shadow-[0_3px_0_hsl(var(--purple-800))]'
                    : 'bg-cream text-deep-800 border-deep-300'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'all' && (
          <AllTab featured={featured} blogs={blogs} tips={knowApp} onOpen={setActiveArticle} />
        )}
        {tab === 'hacks' && <HacksTab tips={knowApp} onOpen={setActiveArticle} />}
        {tab === 'clips' && <ClipsTab videos={videos} />}
        {tab === 'ultra' && (
          <UltraTab items={blogs.filter(isImageCardArticle)} onOpen={setActiveArticle} />
        )}
      </div>

      <Navigation />
    </div>
  );
};

const AllTab = ({
  featured,
  blogs,
  tips,
  onOpen,
}: {
  featured?: LearningContent;
  blogs: LearningContent[];
  tips: LearningContent[];
  onOpen: (a: Article) => void;
}) => {
  const tiles = blogs.filter(isImageCardArticle);
  return (
    <div className="space-y-3">
      {tiles.length ? (
        <PortraitGrid items={tiles} featuredId={featured?.id} onOpen={onOpen} />
      ) : (
        <p className="text-sm text-deep-500">No articles yet.</p>
      )}
      {tips.map((tip) => (
        <TipCard key={tip.id} item={tip} onOpen={() => onOpen(toArticle(tip, 0))} />
      ))}
    </div>
  );
};

/**
 * Mixed image-only and text tiles must size to their own content (`items-start`); stretching
 * the shorter card to the row height leaves its unused part showing as a white strip.
 */
const PortraitGrid = ({
  items,
  featuredId,
  onOpen,
}: {
  items: LearningContent[];
  featuredId?: string;
  onOpen: (a: Article) => void;
}) => (
  <div className="grid grid-cols-2 gap-3 items-start" data-testid="ultra-portrait-grid">
    {items.map((item, i) => {
      if (isClip(item)) {
        return (
          <VideoTile
            key={item.id}
            color={CLIP_COLORS[i % CLIP_COLORS.length]}
            linkUrl={item.linkUrl}
            item={item}
          />
        );
      }
      const isFeaturedListing = Boolean(featuredId && item.id === featuredId);
      return (
        <ArticleTile
          key={item.id}
          item={item}
          cardTestId={isFeaturedListing ? 'featured-blog-card' : undefined}
          thumbTestId={
            isImageCardArticle(item)
              ? isFeaturedListing
                ? 'featured-listing-thumb'
                : 'ultra-listing-thumb'
              : undefined
          }
          onOpen={() => onOpen(toArticle(item, i))}
        />
      );
    })}
  </div>
);

const ArticleTile = ({
  item,
  onOpen,
  cardTestId,
  thumbTestId,
}: {
  item?: LearningContent;
  onOpen?: () => void;
  cardTestId?: string;
  thumbTestId?: string;
}) => {
  const imageOnly = isImageCardArticle(item);
  const label = item?.headline || 'Article';
  return (
    <Card
      data-testid={cardTestId}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={label}
      className={`bg-white border-deep-100 rounded-3xl shadow-sm overflow-hidden ${
        imageOnly ? 'p-0' : 'p-3'
      } ${onOpen ? 'cursor-pointer active:scale-[0.99]' : ''}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (!onOpen) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {imageOnly ? (
        <div
          className="w-full aspect-[4/5] bg-[#7ee7f2] flex items-center justify-center"
          data-testid="ultra-thumb-only"
        >
          <ContentImage
            item={item}
            className="max-h-full w-full h-full object-contain"
            testId={thumbTestId}
            fallback={
              <div className="w-full h-full flex items-center justify-center text-sm font-black italic text-deep-800">
                WEEK END
              </div>
            }
          />
        </div>
      ) : (
        <>
          <div className="inline-block px-2 py-0.5 rounded-full bg-lime-500 text-deep-800 text-[10px] font-extrabold tracking-wide mb-2">
            {item
              ? `${Math.max(3, Math.min(12, Math.ceil((item.bodyText?.length || 800) / 800)))} MIN READ`
              : '9 MIN READ'}
          </div>
          <div className="w-full h-16 rounded-2xl overflow-hidden mb-2 bg-gradient-to-br from-hydro-200 to-lilac-300">
            <ContentImage
              item={item}
              className="w-full h-full object-cover"
              testId={thumbTestId}
              fallback={
                <div className="w-full h-full flex items-center justify-center text-sm font-black italic text-deep-800">
                  WEEK END
                </div>
              }
            />
          </div>
          <h4 className="text-sm font-bold text-deep-800 leading-tight line-clamp-3">{label}</h4>
          <p className="text-[10px] text-deep-500 mt-1 leading-tight line-clamp-2">
            {truncatePreview(item?.bodyText, 50)}
          </p>
        </>
      )}
    </Card>
  );
};

const TipCard = ({
  item,
  title,
  subtitle,
  onOpen,
}: {
  item?: LearningContent;
  title?: string;
  subtitle?: string;
  onOpen?: () => void;
}) => (
  <Card
    data-testid="tip-listing-card"
    className={`bg-white border-deep-100 rounded-3xl p-4 shadow-sm ${onOpen ? 'cursor-pointer active:scale-[0.99]' : ''}`}
    onClick={onOpen}
  >
    <div className="flex gap-3">
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-bold text-deep-800 leading-tight mb-2 line-clamp-3">
          {item?.headline || title}
        </h3>
        <p className="text-[11px] text-deep-500 leading-snug line-clamp-3">
          {item?.subHeading || subtitle || truncatePreview(item?.bodyText, 80)}
        </p>
      </div>
      <div className="w-20 h-20 shrink-0 self-start rounded-xl overflow-hidden min-h-0 bg-gradient-to-br from-lilac-300 to-hydro-200">
        <ContentImage
          item={item}
          className="w-full h-full object-cover"
          fallback={
            <div className="w-full h-full flex items-center justify-center text-3xl">🤚</div>
          }
        />
      </div>
    </div>
  </Card>
);

const VideoTile = ({
  color,
  linkUrl,
  item,
}: {
  color: string;
  linkUrl?: string;
  item?: LearningContent;
}) => (
  <Card
    className={`${color} border-none rounded-3xl p-3 shadow-sm min-h-[180px] relative overflow-hidden ${linkUrl ? 'cursor-pointer' : ''}`}
    onClick={() => linkUrl && window.open(linkUrl, '_blank')}
  >
    {item && contentImageUrl(item) && (
      <img
        src={contentImageUrl(item)!}
        alt={item.headline}
        className="absolute inset-0 w-full h-full object-cover opacity-80"
      />
    )}
    <div className="absolute bottom-3 left-3 bg-white rounded-full pl-1 pr-3 py-1 flex items-center gap-1">
      <div className="w-6 h-6 rounded-full bg-deep-800 flex items-center justify-center">
        <Play className="w-3 h-3 text-white fill-white ml-0.5" />
      </div>
    </div>
  </Card>
);

const HacksTab = ({ tips, onOpen }: { tips: LearningContent[]; onOpen: (a: Article) => void }) => {
  if (!tips.length) {
    return <p className="text-sm text-deep-500">No app tips yet.</p>;
  }

  return (
    <div className="space-y-3">
      {tips.map((tip) => (
        <TipCard key={tip.id} item={tip} onOpen={() => onOpen(toArticle(tip, 0))} />
      ))}
    </div>
  );
};

const ClipsTab = ({ videos }: { videos: LearningContent[] }) => {
  if (!videos.length) {
    return <p className="text-sm text-deep-500">No clips yet.</p>;
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {videos.map((video, i) => (
        <Card
          key={video.id}
          className={`${CLIP_COLORS[i % CLIP_COLORS.length]} border-none rounded-3xl shadow-sm h-44 relative overflow-hidden ${video.linkUrl ? 'cursor-pointer' : ''}`}
          onClick={() => video.linkUrl && window.open(video.linkUrl, '_blank')}
        >
          {contentImageUrl(video) && (
            <img
              src={contentImageUrl(video)!}
              alt={video.headline}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <div className="absolute bottom-3 left-3 bg-white rounded-full pl-1 pr-3 py-1 flex items-center gap-1">
            <div className="w-6 h-6 rounded-full bg-deep-800 flex items-center justify-center">
              <Play className="w-3 h-3 text-white fill-white ml-0.5" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const UltraTab = ({ items, onOpen }: { items: LearningContent[]; onOpen: (a: Article) => void }) =>
  items.length ? (
    <PortraitGrid items={items} onOpen={onOpen} />
  ) : (
    <p className="text-sm text-deep-500">No Ultra cards yet.</p>
  );

const ArticleReader = ({
  article,
  nickname,
  onClose,
}: {
  article: Article;
  nickname: string;
  onClose: () => void;
}) => {
  const hasImage = Boolean(article.imageUrl);
  const plainBody = stripHtml(article.bodyText);
  const hasBody = Boolean(plainBody);
  const isHtml = /<[a-z][\s\S]*>/i.test(article.bodyText || '');
  const imageLed = Boolean(article.imageCard) || (hasImage && !hasBody);

  const paragraphs =
    hasBody && article.bodyText ? article.bodyText.split(/\n\n+/).filter(Boolean) : [];

  const renderBody = () => {
    if (!hasBody) {
      return (
        <p data-testid="article-empty-body" className="text-deep-500">
          Full article text is not available yet.
        </p>
      );
    }
    if (isHtml && article.bodyText) {
      return (
        <div
          data-testid="article-html-body"
          className="quill-content [&>p]:mb-4 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-4 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mb-3 [&>h3]:text-lg [&>h3]:font-bold [&>h3]:mb-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-4 [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:mb-4 [&>blockquote]:border-l-4 [&>blockquote]:border-purple-200 [&>blockquote]:pl-4 [&>blockquote]:italic [&>img]:rounded-xl [&>img]:my-4 [&>img]:max-w-full"
        >
          {parse(article.bodyText)}
        </div>
      );
    }
    return paragraphs.map((p, i) => <p key={i}>{p}</p>);
  };

  return (
    <div className="min-h-screen bg-cream pb-28 overflow-x-hidden" data-testid="article-reader">
      <div className="max-w-md mx-auto">
        <div className="px-5 pt-6">
          <BrandHeader title="Sauce" nickname={nickname} />
        </div>

        <div className="flex items-center justify-between px-5 mt-4">
          <p className="font-semibold text-deep-800">Article</p>
          <button type="button" onClick={onClose} className="p-1" aria-label="Close article">
            <X className="w-5 h-5 text-deep-800" />
          </button>
        </div>

        {hasImage ? (
          <div className="mt-4 w-full bg-[#7ee7f2]" data-testid="article-hero">
            <ContentImage
              item={{ imageUrl: article.imageUrl, headline: article.title }}
              variant="hero"
              className="w-full h-auto object-contain"
              fallback={
                <div className={`${article.heroBg} flex items-center justify-center min-h-[200px]`}>
                  <div className="text-6xl">✋</div>
                </div>
              }
            />
          </div>
        ) : !imageLed ? (
          <div className="mt-4 mx-5 rounded-t-3xl pt-6 pb-20 flex items-center justify-center relative overflow-hidden min-h-[200px]">
            <div className={`absolute inset-0 ${article.heroBg} flex items-center justify-center`}>
              <div className="text-6xl">✋</div>
            </div>
          </div>
        ) : null}

        {!imageLed ? (
          <>
            <div className={`px-5 relative z-10 ${hasImage ? 'mt-4' : '-mt-12'}`}>
              <div className="bg-white rounded-2xl p-5 shadow-lg text-center">
                <h2 className="text-2xl font-extrabold text-purple-700 leading-tight break-words">
                  {article.title}
                </h2>
                <p className="text-xs text-deep-500 mt-2 break-words">{article.subtitle}</p>
                <div className="flex justify-center flex-wrap gap-2 mt-4">
                  {article.tags.map((t) => (
                    <span
                      key={t.label}
                      className={`${t.color} text-deep-800 text-[10px] font-extrabold px-3 py-1.5 rounded-full tracking-wider`}
                    >
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 mt-6 space-y-4 text-deep-700 text-sm leading-relaxed break-words">
              {renderBody()}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Content;
